/**
 * Console filter to suppress known browser extension warnings and other noise
 * This helps keep the console clean for actual application errors
 */

const isDev = process.env.NODE_ENV === 'development';
const isProd = process.env.NODE_ENV === 'production';

// Patterns to filter out from console
const FILTER_PATTERNS = [
  // Browser extension warnings
  /ObjectMultiplex.*orphaned.*publicConfig/i,
  /Extension context invalidated/i,
  /Uncaught.*Extension context/i,
  /content\.js/i,

  // React 18 known warnings (until Ant Design fully updates)
  /findDOMNode is deprecated in StrictMode/i,
  /Warning.*findDOMNode/i,

  // Chrome DevTools warnings
  /DevTools failed to load source map/i,
  /Failed to load resource.*chrome-extension/i,
];

// Store original console methods
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
};

/**
 * Check if a message should be filtered
 */
function shouldFilter(args: any[]): boolean {
  const message = args
    .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');

  return FILTER_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Install console filters
 */
export function installConsoleFilters() {
  // Only filter in production or if explicitly enabled
  if (!isProd && !window.localStorage.getItem('enableConsoleFilter')) {
    return;
  }

  // Override console.warn
  console.warn = function (...args: any[]) {
    if (!shouldFilter(args)) {
      originalConsole.warn.apply(console, args);
    }
  };

  // Override console.error
  console.error = function (...args: any[]) {
    if (!shouldFilter(args)) {
      originalConsole.error.apply(console, args);
    }
  };

  // In production, suppress all console.log
  if (isProd) {
    console.log = function () {
      // Suppress all logs in production
    };
  }
}

/**
 * Install global error handlers
 */
export function installGlobalErrorHandlers() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;

    // Filter out known extension errors
    if (error && error.message && shouldFilter([error.message])) {
      event.preventDefault();
      return;
    }

    // Log the error properly
    originalConsole.error('Unhandled Promise Rejection:', error);

    // In production, report to error tracking service
    if (isProd && window.errorReporter) {
      window.errorReporter.report(error);
    }

    // Prevent default browser behavior
    event.preventDefault();
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    const { message, filename, lineno, colno, error } = event;

    // Filter out extension errors
    if (filename && filename.includes('chrome-extension://')) {
      event.preventDefault();
      return;
    }

    // Filter out known patterns
    if (shouldFilter([message])) {
      event.preventDefault();
      return;
    }

    // Log the error
    originalConsole.error('Global Error:', {
      message,
      filename,
      lineno,
      colno,
      error,
    });

    // In production, report to error tracking service
    if (isProd && window.errorReporter) {
      window.errorReporter.report(error || new Error(message));
    }
  });
}

/**
 * Remove console.log statements in production
 */
export function cleanupConsoleLogs() {
  if (!isProd) return;

  // Suppress console.log in production
  console.log = () => {};

  // Optionally suppress console.debug and console.info
  console.debug = () => {};
  console.info = () => {};
}

/**
 * Initialize all console and error handling improvements
 */
export function initializeConsoleCleanup() {
  installConsoleFilters();
  installGlobalErrorHandlers();
  cleanupConsoleLogs();
}

// Export a helper to restore original console (useful for debugging)
export function restoreConsole() {
  console.log = originalConsole.log;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

// Add to window for debugging purposes
if (isDev) {
  (window as any).restoreConsole = restoreConsole;
  (window as any).originalConsole = originalConsole;
}
